import { skipToken } from "@reduxjs/toolkit/query";
import {
  SIGNAL_CONFIGURATION_AGENT_ID,
  type SignalOperation,
  type TestSignalProposalToolOutput,
} from "@repo/app-types/entities/configurationAgent";
import { defaultDateRange } from "@repo/app-types/entities/dateRange";
import {
  asMetadataFieldId,
  type SignalId,
  type SignalMetadataSchema,
  type SignalMetadataSchemaField,
} from "@repo/app-types/entities/signal";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import { DateTime } from "luxon";
import { type FC, useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { v4 as uuidv4 } from "uuid";
import {
  useCreateSignalMutation,
  useGetSignalQuery,
  useTestSignalProposalMutation,
  useUpdateSignalMutation,
} from "../../../api/entities/signal.js";
import { MastraChatProvider } from "../../agents/mastra/mastraChatContext.js";
import { useMastraChat } from "../../agents/mastra/useMastraChat.js";
import { SignalConfigurationAssistant } from "../../agents/signalConfigurationAssistant/signalConfigurationAssistant.js";
import { SignalEditorContainer } from "./signalEditorContainer.js";
import { cleanMetadataSchema, isSignalFormDirty } from "./signalFormHelpers.js";
import {
  SignalTestConfigModal,
  type TestSignalFiltersUi,
} from "./signalTestConfigModal.js";
import { SignalTestMatchesModal } from "./signalTestMatchesModal.js";

type TestResultsState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error" }
  | {
      status: "complete";
      data: TestSignalProposalToolOutput;
      completedAt: DateTime;
    };

export const SignalEditorPage: FC<{ onClose: () => void }> = ({ onClose }) => {
  const { signalId } = useParams<{ signalId: SignalId }>();
  const isNew = !signalId;

  const {
    data: signal,
    isLoading: isLoadingSignal,
    isError,
  } = useGetSignalQuery(isNew ? skipToken : signalId);
  const [createSignal, { isLoading: isCreating }] = useCreateSignalMutation();
  const [updateSignal, { isLoading: isSaving }] = useUpdateSignalMutation();
  const [testSignalProposal] = useTestSignalProposalMutation();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [metadataSchema, setMetadataSchema] = useState<SignalMetadataSchema>({
    fields: [],
  });
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(isNew);
  const [testResultsState, setTestResultsState] = useState<TestResultsState>({
    status: "idle",
  });
  const [testSignalFilters, setTestSignalFilters] =
    useState<TestSignalFiltersUi>({
      playbookId: null,
      teammateGroupIds: null,
      dateRange: defaultDateRange,
    });
  // Within-session memory of the filters used on the last actual run. When
  // non-null, the main Test signal click re-runs with these directly; otherwise
  // it opens the config modal so the user can pick.
  const [lastRunFilters, setLastRunFilters] =
    useState<TestSignalFiltersUi | null>(null);
  const [isTestConfigOpen, setIsTestConfigOpen] = useState(false);
  const [isMatchesModalOpen, setIsMatchesModalOpen] = useState(false);

  const runTestWithFilters = useCallback(
    async (filters: TestSignalFiltersUi) => {
      if (!name.trim() || !description.trim()) return;

      setTestResultsState({ status: "loading" });

      const now = DateTime.now();
      const rangeEnd = filters.dateRange.end ?? now;
      const rangeStart = rangeEnd.minus(filters.dateRange.length);

      try {
        const data = await testSignalProposal({
          ...(signalId ? { signalId } : {}),
          signal: {
            name,
            description,
            metadata_schema: cleanMetadataSchema(metadataSchema),
          },
          filters: {
            ...(filters.playbookId ? { playbookId: filters.playbookId } : {}),
            ...(filters.teammateGroupIds
              ? { teammateGroupIds: filters.teammateGroupIds }
              : {}),
            lastRecordedAtRange: {
              after: rangeStart.startOf("day").toUTC().toISO(),
              before: rangeEnd.endOf("day").toUTC().toISO(),
            },
          },
        }).unwrap();

        setTestResultsState({
          status: "complete",
          data,
          completedAt: DateTime.now(),
        });
      } catch (err) {
        console.error("Failed to test signal:", err);
        setTestResultsState({ status: "error" });
      }
    },
    [signalId, name, description, metadataSchema, testSignalProposal],
  );

  const handleRunTestFromModal = useCallback(
    (filters: TestSignalFiltersUi) => {
      setIsTestConfigOpen(false);
      setTestSignalFilters(filters);
      setLastRunFilters(filters);
      runTestWithFilters(filters);
    },
    [runTestWithFilters],
  );

  // Open config modal if filters have not been configured yet
  const handleTestSignalClick = useCallback(() => {
    if (lastRunFilters) {
      runTestWithFilters(lastRunFilters);
    } else {
      setIsTestConfigOpen(true);
    }
  }, [lastRunFilters, runTestWithFilters]);

  const handleConfigureTestClick = useCallback(() => {
    setIsTestConfigOpen(true);
  }, []);

  const chat = useMastraChat({
    agentId: SIGNAL_CONFIGURATION_AGENT_ID,
    onSend: async () => {
      const draftSignal = isSignalFormDirty(
        signal,
        name,
        description,
        metadataSchema,
      )
        ? {
            name,
            description,
            metadataSchema,
          }
        : undefined;

      // Convert UI date range to ISO date strings for the agent
      const now = DateTime.now();
      const rangeEnd = testSignalFilters.dateRange.end ?? now;
      const rangeStart = rangeEnd.minus(testSignalFilters.dateRange.length);
      const filters = {
        ...(testSignalFilters.playbookId
          ? { playbookId: testSignalFilters.playbookId }
          : {}),
        ...(testSignalFilters.teammateGroupIds
          ? { teammateGroupIds: testSignalFilters.teammateGroupIds }
          : {}),
        lastRecordedAtRange: {
          after: rangeStart.startOf("day").toUTC().toISO(),
          before: rangeEnd.endOf("day").toUTC().toISO(),
        },
      };

      return {
        requestContext: {
          signalId,
          draftSignal,
          testSignalFilters: filters,
        },
      };
    },
  });

  useEffect(() => {
    if (signal && !initialized) {
      setName(signal.name);
      setDescription(signal.description);
      setMetadataSchema(signal.metadata_schema);
      setInitialized(true);
    }
  }, [signal, initialized]);

  const handleApplyOperation = useCallback(
    async (operation: SignalOperation) => {
      switch (operation.type) {
        case "update-signal":
          if (operation.name !== undefined) {
            setName(operation.name);
          }
          if (operation.description !== undefined) {
            setDescription(operation.description);
          }
          break;
        case "add-metadata-field":
          setMetadataSchema((prev) => {
            // Don't add if a field with the same display key already exists
            if (prev.fields.some((f) => f.key === operation.key)) return prev;
            const id = asMetadataFieldId(uuidv4());
            const newField: SignalMetadataSchemaField =
              operation.fieldType === "enum"
                ? {
                    id,
                    key: operation.key,
                    type: "enum",
                    options: operation.options ?? [],
                  }
                : {
                    id,
                    key: operation.key,
                    type: "number",
                  };
            return { fields: [...prev.fields, newField] };
          });
          break;
        case "update-metadata-field":
          setMetadataSchema((prev) => ({
            fields: prev.fields.map((f) => {
              if (f.key !== operation.key || f.type !== "enum") return f;
              let options = f.options;
              if (operation.removeOptions) {
                const removeSet = new Set(operation.removeOptions);
                options = options.filter((o) => !removeSet.has(o));
              }
              if (operation.addOptions) {
                const existingSet = new Set(options);
                const newOptions = operation.addOptions.filter(
                  (o) => !existingSet.has(o),
                );
                options = [...options, ...newOptions];
              }
              return { ...f, options };
            }),
          }));
          break;
        case "remove-metadata-field":
          setMetadataSchema((prev) => ({
            fields: prev.fields.filter((f) => f.key !== operation.key),
          }));
          break;
      }
    },
    [],
  );

  if (!isNew && (isLoadingSignal || !initialized)) {
    return (
      <div className="flex flex-col">
        <PageHeader width={PageWidth.Form} isLoading />
      </div>
    );
  }

  if (!isNew && (isError || !signal)) {
    return (
      <div className="flex flex-col">
        <PageHeader width={PageWidth.Form} title="Signal not found" />
      </div>
    );
  }

  const isFormValid = name.trim() !== "" && description.trim() !== "";
  const hasChanged =
    !isNew &&
    signal != null &&
    isSignalFormDirty(signal, name, description, metadataSchema);

  const handleSubmit = async () => {
    if (!isFormValid) return;
    if (!isNew && !hasChanged) return;

    const payload = {
      name: name.trim(),
      description: description.trim(),
      metadata_schema: cleanMetadataSchema(metadataSchema),
    };

    try {
      if (isNew) {
        await createSignal(payload).unwrap();
      } else {
        await updateSignal({ id: signal!.id, ...payload }).unwrap();
      }
      onClose();
    } catch (err: any) {
      const action = isNew ? "create" : "update";
      console.error(`Failed to ${action} signal:`, err);
      setError(
        err.data?.message || `Failed to ${action} signal. Please try again.`,
      );
    }
  };

  const handleDiscard = () => {
    if (!signal) return;
    setName(signal.name);
    setDescription(signal.description);
    setMetadataSchema(signal.metadata_schema);
    setError(null);
  };

  const testResultPillState =
    testResultsState.status === "idle" ? null : testResultsState;

  return (
    <MastraChatProvider chat={chat}>
      <div className="flex h-full">
        <div className="min-h-0 flex-1">
          <SignalEditorContainer
            name={name}
            description={description}
            metadataSchema={metadataSchema}
            error={error}
            onNameChange={setName}
            onDescriptionChange={setDescription}
            onMetadataSchemaChange={setMetadataSchema}
            onClose={onClose}
            onDiscard={isNew ? undefined : handleDiscard}
            onSubmit={handleSubmit}
            isLoading={isNew ? isCreating : isSaving}
            isSubmitDisabled={
              isNew ? !isFormValid : !isFormValid || !hasChanged
            }
            submitLabel={isNew ? "Create signal" : "Save"}
            showDiscard={!isNew && hasChanged}
            onTestSignalClick={handleTestSignalClick}
            onConfigureTestClick={handleConfigureTestClick}
            isTestSignalDisabled={
              !isFormValid || testResultsState.status === "loading"
            }
            testResultPillState={testResultPillState}
            onViewMatches={() => setIsMatchesModalOpen(true)}
          />
        </div>
        <div className="flex min-h-0 flex-col border-l border-border-light">
          <SignalConfigurationAssistant
            name={name}
            description={description}
            metadataSchema={metadataSchema}
            onApplyOperation={handleApplyOperation}
            onTestResultsLoading={() =>
              setTestResultsState({ status: "loading" })
            }
            onTestResultsComplete={(data) =>
              setTestResultsState({
                status: "complete",
                data,
                completedAt: DateTime.now(),
              })
            }
          />
        </div>
      </div>

      <SignalTestConfigModal
        isOpen={isTestConfigOpen}
        onClose={() => setIsTestConfigOpen(false)}
        initialFilters={testSignalFilters}
        onRunTest={handleRunTestFromModal}
      />

      {testResultsState.status === "complete" ? (
        <SignalTestMatchesModal
          isOpen={isMatchesModalOpen}
          onClose={() => setIsMatchesModalOpen(false)}
          data={testResultsState.data}
          metadataSchema={metadataSchema}
        />
      ) : null}
    </MastraChatProvider>
  );
};
